import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { getDeliveryPricingToken } from '../../../state/order/hooks/useGetDeliveryPricingToken';
import { useOrder } from '../../../state/order/hooks/useOrder';
import { useOrderRestaurant } from '../../../state/order/hooks/useOrderRestaurant';
import { usePushToOrderProcessor } from '../../../state/order/hooks/usePushToOrderProcessor';
import { PushQueueResult } from '../../../state/order/OrderProcessor';
import { OrderType } from '../../../types';
import { SECOND_IN_MS } from '../../../utils/utils';
import { useOrderValidation } from '../../../state/order/hooks/useOrderValidation';
import { Path } from '../../../routes/components/path';

export const useDeliveryPrice = (): {
  isDeliveryPriceChanged?: boolean;
  currentDeliveryPrice?: number;
  lastUpdatedAt?: string | number;
} => {
  const order = useOrder();
  const { restaurant } = useOrderRestaurant();
  useDeliveryPriceUpdater();

  const updatePrevDeliveryPrice = useUpdatePrevDeliveryPrice();

  const previousToken = order.getPrevDeliveryTokenData();
  const currentToken = order.getDeliveryTokenData();

  if (
    currentToken?.shopId !== restaurant?.restaurantId &&
    currentToken?.shopId !== restaurant?.deliveryRedirectionRestaurant?.restaurantId
  ) {
    return {};
  }

  const previousDeliveryPrice = previousToken?.deliveryFee;
  const currentDeliveryPrice = currentToken?.deliveryFee;
  const lastUpdatedAt = currentToken?.iat;

  const isDeliveryPriceChanged = Boolean(previousDeliveryPrice !== currentDeliveryPrice);

  if (isDeliveryPriceChanged && currentDeliveryPrice != undefined) {
    updatePrevDeliveryPrice();
  }

  return {
    isDeliveryPriceChanged,
    currentDeliveryPrice: currentDeliveryPrice || undefined,
    lastUpdatedAt,
  };
};

const useDeliveryPriceUpdater = (): void => {
  const [isInitialUpdated, setIsInitialUpdated] = useState(false);
  const pathname = usePathname();

  const updateDeliveryPrice = useUpdateDeliveryPrice();
  useEffect(() => {
    // The /app-payment page receives its order from the native bridge — delivery price polling
    // races with the bridge handshake and can overwrite the real order with stale data.
    if (pathname === Path.AppWebViewOrderPayment) return;

    if (!isInitialUpdated) {
      void (async (): Promise<void> => {
        try {
          await updateDeliveryPrice();
        } catch {
          // Errors are already surfaced via OrderProcessor's addError/trackInsightsException.
        } finally {
          queueMicrotask(() => {
            setIsInitialUpdated(true);
          });
        }
      })();
      return;
    }

    // Update delivery price on idle before it expires.
    const timer = setInterval(
      () => {
        void (async (): Promise<void> => {
          try {
            await updateDeliveryPrice();
          } catch {
            // Errors are already surfaced via OrderProcessor's addError/trackInsightsException.
          }
        })();
      },
      process.env.CI === 'true' ? SECOND_IN_MS * 60 : SECOND_IN_MS * 10
    );
    return () => clearInterval(timer);
  }, [updateDeliveryPrice, isInitialUpdated, pathname]);
};

const useUpdateDeliveryPrice = (): (() => Promise<PushQueueResult<void> | void>) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const order = useOrder();
  const pushToQueue = usePushToOrderProcessor<void>();
  const validateOrder = useOrderValidation();

  // order is a mutable class instance from OrderContext that gets a new reference on nearly
  // every update. Depending on it directly here previously meant this callback (and therefore
  // useDeliveryPriceUpdater's effect, which depends on it) got a new identity on almost every
  // render — tearing down and rebuilding a setInterval on every single one, at the app root
  // (this hook is used in AppWrapper). Under a burst of renders from anywhere in the app, that
  // churn alone could saturate the main thread. Read the latest order/isUpdating through refs
  // instead, so the callback's identity only depends on the genuinely stable pushToQueue/
  // validateOrder — order is still always read fresh at call time.
  const orderRef = useRef(order);
  orderRef.current = order;
  const isUpdatingRef = useRef(isUpdating);
  isUpdatingRef.current = isUpdating;

  const updateDeliveryPrice = useCallback(async (): Promise<PushQueueResult<void> | void> => {
    const order = orderRef.current;
    if (order.type !== OrderType.DELIVERY) return;
    if (!order.restaurantId) return;

    const nowInUnixSeconds = Date.now() / 1000;
    const tokenData = order.getDeliveryTokenData();
    // Check usableUntil unix seconds from token and refresh token if it's passed.
    if (tokenData?.usableUntil && tokenData.usableUntil > nowInUnixSeconds) return;
    if (isUpdatingRef.current) return;

    setIsUpdating(true);

    return pushToQueue({
      action: async ({ order }) => {
        try {
          const newOrder = order.clone();
          if (newOrder.restaurantId && newOrder.zoneId) {
            try {
              const deliveryPriceToken = await getDeliveryPricingToken(
                newOrder.restaurantId,
                newOrder.zoneId,
                newOrder.preorderTime?.format()
              );
              newOrder.deliveryPriceToken = deliveryPriceToken;
            } catch (error) {
              // Reset restaurant selection in case delivery token creation failed.
              newOrder.restaurantId = null;
              newOrder.deliveryPriceToken = null;
              newOrder.prevDeliveryPriceToken = null;
            }
          } else {
            newOrder.deliveryPriceToken = null;
            newOrder.prevDeliveryPriceToken = null;
          }

          const validationResult = await validateOrder(newOrder);
          const errors = newOrder.applyOrderValidation(validationResult);

          return {
            order: newOrder,
            validationResult: validationResult,
            error: errors,
          };
        } finally {
          setIsUpdating(false);
        }
      },
    });
  }, [pushToQueue, validateOrder]);

  return updateDeliveryPrice;
};

const useUpdatePrevDeliveryPrice = (): (() => Promise<PushQueueResult<void> | void>) => {
  const pushToQueue = usePushToOrderProcessor<void>();

  return useCallback(async (): Promise<PushQueueResult<void> | void> => {
    return pushToQueue({
      action: async ({ order }) => {
        const newOrder = order.clone();
        if (newOrder.restaurantId) {
          newOrder.updatePrevDeliveryToken();
        } else {
          newOrder.deliveryPriceToken = null;
          newOrder.prevDeliveryPriceToken = null;
        }

        return {
          order: newOrder,
          validationResult: null,
          error: [],
        };
      },
    });
  }, [pushToQueue]);
};
